/* eslint-disable react-refresh/only-export-components -- co-located helpers/constants */
import React, { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Box,
  Collapse,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import { AppIconButton } from '../../design-system';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { NotesSectionHeader, type DecoratedSectionIcon } from '../../notes/NotesSection';
import VerticalResizeHandle from './VerticalResizeHandle';
import { useHorizontalPaneResize } from './useHorizontalPaneResize';
import { panelDividerBg } from './panelDividerBg';
import { getPanelResizeBackdrop } from '../../../theme/uiSemanticColors';

export { panelDividerBg };

export {
  default as DetailPanelVerticalTabs,
  type DetailPanelTabConfig,
  type DetailPanelVerticalTabsProps,
} from './DetailPanelVerticalTabs';

export {
  default as DetailPanelMetadataField,
  type DetailPanelMetadataFieldProps,
} from './DetailPanelMetadataField';

export {
  default as DetailPanelReadOnlyField,
  type DetailPanelReadOnlyFieldProps,
} from './DetailPanelReadOnlyField';

export {
  default as DetailPanelLoadingOverlay,
  type DetailPanelLoadingOverlayProps,
} from './DetailPanelLoadingOverlay';

const DEFAULT_PANEL_WIDTH = 480;
const MIN_PANEL_WIDTH = 280;
const MAX_PANEL_WIDTH = 960;
const MIN_TABLE_WIDTH = 320;
const MAX_TABLE_WIDTH = 1200;

function parsePxWidth(value: number | string | undefined): number | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export const SectionSeparator: React.FC = () => (
  <Box
    role="separator"
    aria-hidden
    sx={{
      height: '1px',
      width: '100%',
      flexShrink: 0,
      bgcolor: panelDividerBg,
    }}
  />
);

export const TableDetailSplitSeparator: React.FC = () => (
  <>
    <Box
      role="separator"
      aria-hidden
      sx={{
        display: { xs: 'block', lg: 'none' },
        height: '1px',
        width: '100%',
        flexShrink: 0,
        bgcolor: panelDividerBg,
      }}
    />
    <Box
      role="separator"
      aria-hidden
      sx={{
        display: { xs: 'none', lg: 'block' },
        width: '1px',
        flexShrink: 0,
        alignSelf: 'stretch',
        bgcolor: panelDividerBg,
      }}
    />
  </>
);

export interface DetailPanelSectionProps {
  title: string;
  description?: string;
  headerAction?: ReactNode;
  /** Decorated header with icon (notes, attachments, images, etc.). */
  sectionIcon?: DecoratedSectionIcon;
  /** @deprecated Use `sectionIcon="notes"` */
  notesDecoration?: boolean;
  /** When true, section body can be expanded/collapsed via the header. */
  collapsible?: boolean;
  /** Initial expanded state when `collapsible` is true. Default true. */
  defaultExpanded?: boolean;
  /** Controlled expanded state (use with `onExpandedChange`). */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  children: ReactNode;
}

export const DetailPanelSection: React.FC<DetailPanelSectionProps> = ({
  title,
  description,
  headerAction,
  sectionIcon,
  notesDecoration = false,
  collapsible = false,
  defaultExpanded = true,
  expanded: expandedProp,
  onExpandedChange,
  children,
}) => {
  const resolvedIcon = sectionIcon ?? (notesDecoration ? 'notes' : undefined);
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : internalExpanded;

  const setExpanded = (value: boolean | ((prev: boolean) => boolean)) => {
    const next = typeof value === 'function' ? value(expanded) : value;
    if (isControlled) {
      onExpandedChange?.(next);
    } else {
      setInternalExpanded(next);
    }
  };

  useEffect(() => {
    if (!isControlled) {
      setInternalExpanded(defaultExpanded);
    }
  }, [defaultExpanded, isControlled]);

  const headerActionSlot = headerAction ? (
    <Box
      component="span"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      sx={{ display: 'inline-flex' }}
    >
      {headerAction}
    </Box>
  ) : null;

  const expandToggle = collapsible ? (
    <AppIconButton
      size="small"
      aria-label={expanded ? 'Collapse section' : 'Expand section'}
      aria-expanded={expanded}
      onClick={(e) => {
        e.stopPropagation();
        setExpanded((v) => !v);
      }}
      sx={{ p: 0.25, mt: resolvedIcon || description ? 0.25 : 0, flexShrink: 0 }}
    >
      <ExpandMoreIcon
        fontSize="small"
        sx={{
          transform: expanded ? 'rotate(180deg)' : 'rotate(-90deg)',
          transition: 'transform 0.2s ease',
        }}
      />
    </AppIconButton>
  ) : null;

  const headerContent = resolvedIcon ? (
    <NotesSectionHeader
      title={title}
      description={description}
      icon={resolvedIcon}
      actions={headerActionSlot}
      sx={{ mb: 0, flex: 1, minWidth: 0 }}
    />
  ) : (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 1.5,
        flex: 1,
        minWidth: 0,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight={600} component="div">
          {title}
        </Typography>
        {description ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            {description}
          </Typography>
        ) : null}
      </Box>
      {headerActionSlot}
    </Box>
  );

  const header = (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={0.5}
      onClick={collapsible ? () => setExpanded((v) => !v) : undefined}
      sx={
        collapsible
          ? {
              cursor: 'pointer',
              borderRadius: 1,
              mx: -0.5,
              px: 0.5,
              py: 0.25,
              '&:hover': { bgcolor: 'action.hover' },
            }
          : undefined
      }
    >
      {expandToggle}
      {headerContent}
    </Stack>
  );

  return (
    <Box sx={{ py: collapsible ? 1.5 : 2.5 }}>
      <Box sx={{ mb: expanded || !collapsible ? 1.5 : 0 }}>{header}</Box>
      {collapsible ? (
        <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
          <Box sx={{ pb: 0.5 }}>{children}</Box>
        </Collapse>
      ) : (
        children
      )}
    </Box>
  );
};

export interface DetailSplitLockedTablePaneProps {
  locked: boolean;
  children: ReactNode;
}

/** Blurs and blocks pointer events on the table while the detail panel is being edited. */
export const DetailSplitLockedTablePane: React.FC<DetailSplitLockedTablePaneProps> = ({
  locked,
  children,
}) => (
  <Box
    sx={{
      position: 'relative',
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        transition: (theme) => theme.transitions.create('filter'),
        ...(locked && {
          filter: 'blur(1px)',
          pointerEvents: 'none',
          userSelect: 'none',
        }),
      }}
    >
      {children}
    </Box>
    {locked ? (
      <Box
        role="presentation"
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 10,
          cursor: 'not-allowed',
          bgcolor: (theme) => getPanelResizeBackdrop(theme),
        }}
      />
    ) : null}
  </Box>
);

export interface TableDetailSplitLayoutProps {
  table: ReactNode;
  panel: ReactNode;
  /** Blur and block interaction with the table (e.g. while detail panel is in edit mode). */
  tableLocked?: boolean;
  /** Fixed panel width on lg+ (px). Used as initial width when resizable. */
  panelWidth?: number;
  /** Fixed table width on lg+ (px). When set, resize adjusts the table column. */
  tableWidth?: number | string;
  /** Allow drag on the vertical splitter (lg+). Default true. */
  resizable?: boolean;
  defaultPanelWidth?: number;
  minPanelWidth?: number;
  maxPanelWidth?: number;
  minTableWidth?: number;
  maxTableWidth?: number;
  sx?: SxProps<Theme>;
  /** Optional styles for the vertical resize handle (lg+). */
  resizeHandleSx?: SxProps<Theme>;
}

/**
 * Table on the left, detail panel on the right (stacked on xs).
 * Optional draggable vertical splitter on lg+.
 */
export const TableDetailSplitLayout: React.FC<TableDetailSplitLayoutProps> = ({
  table,
  panel,
  tableLocked = false,
  panelWidth: panelWidthProp,
  tableWidth: tableWidthProp,
  resizable = true,
  defaultPanelWidth = DEFAULT_PANEL_WIDTH,
  minPanelWidth = MIN_PANEL_WIDTH,
  maxPanelWidth = MAX_PANEL_WIDTH,
  minTableWidth = MIN_TABLE_WIDTH,
  maxTableWidth = MAX_TABLE_WIDTH,
  sx,
  resizeHandleSx,
}) => {
  const parsedTableWidth = parsePxWidth(tableWidthProp);
  const resizeTable = parsedTableWidth != null;

  const [panelWidth, setPanelWidth] = useState(
    () => panelWidthProp ?? defaultPanelWidth,
  );
  const [tableWidth, setTableWidth] = useState(
    () => parsedTableWidth ?? 520,
  );

  const getStartWidth = useCallback(
    () => (resizeTable ? tableWidth : panelWidth),
    [panelWidth, resizeTable, tableWidth],
  );
  const setActiveWidth = resizeTable ? setTableWidth : setPanelWidth;
  const minWidth = resizeTable ? minTableWidth : minPanelWidth;
  const maxWidth = resizeTable ? maxTableWidth : maxPanelWidth;

  const handleResize = useHorizontalPaneResize({
    enabled: resizable,
    minWidth,
    maxWidth,
    getStartWidth,
    onWidthChange: setActiveWidth,
    panelOnRight: !resizeTable,
  });

  const tablePaneSx = useMemo(() => {
    if (resizeTable) {
      return {
        width: { xs: '100%', lg: tableWidth },
        maxWidth: { xs: '100%', lg: tableWidth },
        flexShrink: 0,
      };
    }
    return { flex: 1, minWidth: 0 };
  }, [resizeTable, tableWidth]);

  const panelPaneSx = useMemo(() => {
    if (resizeTable) {
      return { flex: 1, minWidth: 0, width: { xs: '100%', lg: 'auto' } };
    }
    if (resizable || panelWidthProp != null) {
      return {
        width: { xs: '100%', lg: panelWidth },
        maxWidth: { xs: '100%', lg: panelWidth },
        flexShrink: 0,
      };
    }
    return { flex: 1, minWidth: 0, width: { xs: '100%', lg: 'auto' } };
  }, [panelWidth, panelWidthProp, resizable, resizeTable]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        alignItems: 'stretch',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        overflow: 'hidden',
        gap: { xs: 3, lg: resizable ? 3 : 4 },
        ...sx,
      }}
    >
      <Box
        sx={{
          ...tablePaneSx,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <DetailSplitLockedTablePane locked={tableLocked}>{table}</DetailSplitLockedTablePane>
      </Box>

      {resizable ? (
        <VerticalResizeHandle onMouseDown={handleResize} sx={resizeHandleSx} />
      ) : (
        <TableDetailSplitSeparator />
      )}

      <Box
        sx={{
          ...panelPaneSx,
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {panel}
      </Box>
    </Box>
  );
};

export default TableDetailSplitLayout;
